import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Tabs } from 'antd'
import { Voucher, VoucherQueryParams } from '../../../../pages/reporting/vouchers/types'
import AttacmentModal from '../../../../pages/reporting/vouchers/components/AttacmentModal'
import VoucherTable from './VoucherTable'
import TransactionsTable from './TransactionsTable'

export interface VoucherViewProps {
  /** Alkuarvot haulle - grid-filtterit voivat ylikirjoittaa osan (from, until, voucherType, jne.) */
  initialParams?: Partial<VoucherQueryParams>
  /** Yrityksen ID - jos annettu, ohittaa Redux-kontekstin yrityksen */
  companyId?: string
  /** Dimension query string */
  dimensions?: string
  /** Taulukon korkeus */
  height?: number | string
  /** Näytä action-sarake (edit, copy, delete, attachments) */
  showActions?: boolean
  /** Näytä sarakekohtaiset filtterit (default: true) */
  showFilters?: boolean
  /** Callback kun tosite poistetaan */
  deleteVoucher?: (value: Voucher) => void
  /** Callback kun tositetta muokataan */
  editVoucher?: (value: Voucher) => void
  /** Callback kun tosite kopioidaan */
  copyVoucher?: (value: Voucher) => void
  /** Callback kun data on ladattu */
  onDataLoaded?: () => void
  /** Callback kun gridin filtterit muuttuvat - palauttaa yhdistetyt parametrit (initialParams + grid filtterit) */
  onExportParamsChange?: (params: Partial<VoucherQueryParams>) => void
  /** Muuta tätä pakottaaksesi uudelleenlatauksen */
  refreshKey?: number
}

const VoucherView: React.FC<VoucherViewProps> = ({
  initialParams,
  companyId,
  dimensions,
  height = 800,
  showActions = false,
  showFilters = true,
  deleteVoucher,
  editVoucher,
  copyVoucher,
  onDataLoaded,
  onExportParamsChange,
  refreshKey
}) => {
  const { t } = useTranslation()
  const [attachmentVoucher, setAttachmentVoucher] = useState<Voucher>()
  const [activeTab, setActiveTab] = useState('vouchers')
  const [transactionsInitialized, setTransactionsInitialized] = useState(false)

  const handleTabChange = useCallback((key: string) => {
    setActiveTab(key)
    if (key === 'transactions') setTransactionsInitialized(true)
  }, [])

  const handleAttachmentClose = useCallback(() => {
    setAttachmentVoucher(undefined)
  }, [])

  const handleOpenAttachments = useCallback((voucher: Voucher) => {
    setAttachmentVoucher(voucher)
  }, [])

  const tabItems = [
    {
      key: 'vouchers',
      label: t('global:vouchers'),
      children: (
        <VoucherTable
          initialParams={initialParams}
          companyId={companyId}
          dimensions={dimensions}
          height={height}
          showActions={showActions}
          showFilters={showFilters}
          deleteVoucher={deleteVoucher}
          editVoucher={editVoucher}
          copyVoucher={copyVoucher}
          onDataLoaded={onDataLoaded}
          onExportParamsChange={onExportParamsChange}
          refreshKey={refreshKey}
          onOpenAttachments={handleOpenAttachments}
        />
      )
    },
    {
      key: 'transactions',
      label: t('global:transactions'),
      children: transactionsInitialized ? (
        <TransactionsTable
          initialParams={initialParams}
          companyId={companyId}
          dimensions={dimensions}
          height={height}
          showFilters={showFilters}
          refreshKey={refreshKey}
        />
      ) : null
    }
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <AttacmentModal voucher={attachmentVoucher} onClose={handleAttachmentClose} />
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
    </div>
  )
}

export default VoucherView
